import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Globe, Send, Clock, Zap } from 'lucide-react';
import LiveDate from '../components/LiveDate';
import './Contact.css';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = e => {
    e.preventDefault();
    const waText = `Hi Rajesh! I'm ${form.name}.%0AEmail: ${form.email}%0APhone: ${form.phone}%0AService: ${form.service}%0AMessage: ${form.message}`;
    window.open(`https://wa.me/917907038984?text=${waText}`, '_blank');
    setSubmitted(true);
  };

  return (
    <>
      <Helmet>
        <title>Contact Rajesh R | AEO & AI Optimization Expert Kerala | +91 7907038984</title>
        <meta name="description" content="Contact Rajesh R for AEO, GEO, and AI optimization services in Kerala. Call +91 7907038984 or WhatsApp for a free AI visibility audit." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Rajesh R - AEO Expert Kerala",
          "description": "Contact page for AI Platform Optimization services",
          "mainEntity": {
            "@type": "Person",
            "name": "Rajesh R",
            "telephone": "+917907038984",
            "email": "info@rajeshrnair.com",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Thiruvananthapuram",
              "addressRegion": "Kerala",
              "addressCountry": "IN"
            }
          }
        })}</script>
      </Helmet>

      <div className="page-hero">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="sp-live-bar">
              <span className="live-dot"></span>
              <span>Available Now — </span>
              <LiveDate showTime className="hero-live-date" />
            </div>
            <div className="badge" style={{ marginBottom: '16px' }}><Zap size={12} /> Free Consultation Available</div>
            <h1 className="sp-title">Let's <span className="gradient-text">Talk AI Visibility</span></h1>
            <p className="section-subtitle">
              Get a FREE AI visibility audit for your business. I'll analyze where you stand in ChatGPT, Gemini,
              and Perplexity — and show you exactly how to dominate AI search in your market.
            </p>
          </motion.div>
        </div>
      </div>

      <section style={{ padding: '72px 0' }}>
        <div className="container">
          <div className="contact-grid">
            <motion.div className="contact-form-side" initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              {!submitted ? (
                <form onSubmit={handleSubmit} className="contact-form">
                  <h2 style={{ marginBottom: '8px' }}>Get Your Free <span className="gradient-text">AI Audit</span></h2>
                  <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '28px' }}>
                    Fill this form and I'll send you a detailed AI visibility report within 24 hours.
                  </p>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Your Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} required placeholder="John Abraham" />
                    </div>
                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input name="phone" value={form.phone} onChange={handleChange} required placeholder="+91 98765 43210" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="you@company.com" />
                  </div>
                  <div className="form-group">
                    <label>Service Interested In</label>
                    <select name="service" value={form.service} onChange={handleChange}>
                      <option value="">Select a service...</option>
                      <option>GEO – Generative Engine Optimization</option>
                      <option>AEO – Answer Engine Optimization</option>
                      <option>AIO – AI Optimization</option>
                      <option>LLMO – LLM Optimization</option>
                      <option>GAIO – Generative AI Optimization</option>
                      <option>AISO – AI Search Optimization</option>
                      <option>Full AI Optimization Suite</option>
                      <option>Not sure – need consultation</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Your Message</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={4}
                      placeholder="Tell me about your business and what you want to achieve with AI visibility..." />
                  </div>
                  <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '16px' }}>
                    <Send size={16} /> Send via WhatsApp
                  </button>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '12px' }}>
                    This will open WhatsApp with your message pre-filled for instant delivery.
                  </p>
                </form>
              ) : (
                <div className="form-success">
                  <div className="success-icon">✅</div>
                  <h3>Message Sent!</h3>
                  <p>Your WhatsApp message has been sent. Rajesh will respond within a few hours. You can also call directly at <a href="tel:+917907038984">+91 79070 38984</a>.</p>
                  <button onClick={() => setSubmitted(false)} className="btn-secondary" style={{ marginTop: '16px' }}>
                    Send Another Message
                  </button>
                </div>
              )}
            </motion.div>

            <motion.div className="contact-info-side" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}>
              <div className="contact-quick-links">
                <a href="https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20filled%20out%20your%20Contact%20page%20form%20and%20I%E2%80%99d%20love%20a%20FREE%20AI%20visibility%20audit%20for%20my%20business.%20When%20are%20you%20available%3F" target="_blank" rel="noreferrer" className="quick-contact-btn whatsapp">
                  <div className="qcb-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="qcb-label">WhatsApp</div>
                    <div className="qcb-value">+91 79070 38984</div>
                  </div>
                </a>
                <a href="tel:+917907038984" className="quick-contact-btn call">
                  <div className="qcb-icon"><Phone size={22} /></div>
                  <div>
                    <div className="qcb-label">Call Direct</div>
                    <div className="qcb-value">+91 79070 38984</div>
                  </div>
                </a>
                <a href="mailto:info@rajeshrnair.com" className="quick-contact-btn email">
                  <div className="qcb-icon"><Mail size={22} /></div>
                  <div>
                    <div className="qcb-label">Email</div>
                    <div className="qcb-value">info@rajeshrnair.com</div>
                  </div>
                </a>
              </div>

              <div className="contact-details-card">
                <h3>Contact Details</h3>
                <div className="contact-detail">
                  <MapPin size={16} color="var(--accent-cyan)" />
                  <div>
                    <strong>Location</strong>
                    <p>Thiruvananthapuram, Kerala, India<br />Serving clients globally</p>
                  </div>
                </div>
                <div className="contact-detail">
                  <Clock size={16} color="var(--accent-purple)" />
                  <div>
                    <strong>Response Time</strong>
                    <p>WhatsApp: Usually within 2 hours<br />Email: Within 24 hours</p>
                  </div>
                </div>
                <div className="contact-detail">
                  <Globe size={16} color="var(--accent-green)" />
                  <div>
                    <strong>Online Profiles</strong>
                    <a href="https://rajeshrnair.com" target="_blank" rel="noreferrer">rajeshrnair.com</a>
                    <a href="https://www.linkedin.com/in/rajesh8129355259/" target="_blank" rel="noreferrer">LinkedIn Profile</a>
                  </div>
                </div>
              </div>

              <div className="services-covered-card">
                <h4>Services Available</h4>
                <div className="services-mini-list">
                  {['GEO', 'AEO', 'AIO', 'CAO', 'LLMO', 'GAIO', 'AISO', 'SRO', 'QEO', 'GSO', 'RAO', 'AAO'].map(s => (
                    <span key={s} className="service-mini-tag">{s}</span>
                  ))}
                </div>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px' }}>
                  Serving all 14 Kerala districts + global clients
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
